/**
 * Condition-aware medication suggestions surfaced during the "Add medication"
 * flow. These are conservative, common-practice picks — typical first-line
 * or standard-of-care options — so a user can tap one instead of typing the
 * name + dosage from memory. Suggestions are merged across the user's active
 * conditions, deduped by name, and capped so the UI stays scannable.
 *
 * Not medical advice; not prescribing. The user can still override dosage
 * and frequency when they accept a suggestion.
 */

export interface MedSuggestion {
  name: string
  dosage?: string
  frequency?: string
  category?: string
}

const ENDO_ADENO: MedSuggestion[] = [
  { name: 'Ibuprofen', dosage: '400–600 mg', frequency: 'as needed', category: 'NSAID' },
  { name: 'Naproxen', dosage: '250–500 mg', frequency: 'twice daily', category: 'NSAID' },
  { name: 'Combined oral contraceptive', frequency: 'daily', category: 'Hormonal' },
  { name: 'Norethindrone', dosage: '5 mg', frequency: 'daily', category: 'Progestin' },
  { name: 'Levonorgestrel IUD (Mirena)', category: 'IUD' },
  { name: 'Elagolix (Orilissa)', dosage: '150 mg', frequency: 'daily', category: 'GnRH' },
]

const PMOS: MedSuggestion[] = [
  { name: 'Metformin', dosage: '500–1000 mg', frequency: 'twice daily', category: 'Insulin sensitizer' },
  { name: 'Spironolactone', dosage: '50–100 mg', frequency: 'daily', category: 'Anti-androgen' },
  { name: 'Combined oral contraceptive', frequency: 'daily', category: 'Hormonal' },
  { name: 'Inositol (myo + D-chiro)', frequency: 'daily', category: 'Supplement' },
  { name: 'Letrozole', dosage: '2.5 mg', frequency: 'cycle day 3–7', category: 'Fertility' },
]

const FIBROIDS: MedSuggestion[] = [
  { name: 'Tranexamic acid', dosage: '1000–1300 mg', frequency: 'during period', category: 'Antifibrinolytic' },
  { name: 'Combined oral contraceptive', frequency: 'daily', category: 'Hormonal' },
  { name: 'Levonorgestrel IUD (Mirena)', category: 'IUD' },
  { name: 'Relugolix (Myfembree)', frequency: 'daily', category: 'GnRH' },
  { name: 'Ibuprofen', dosage: '400–600 mg', frequency: 'as needed', category: 'NSAID' },
]

const CPP: MedSuggestion[] = [
  { name: 'Amitriptyline', dosage: '10–25 mg', frequency: 'at bedtime', category: 'Nerve pain' },
  { name: 'Nortriptyline', dosage: '10–25 mg', frequency: 'at bedtime', category: 'Nerve pain' },
  { name: 'Gabapentin', dosage: '100–300 mg', frequency: 'three times daily', category: 'Nerve pain' },
  { name: 'Duloxetine', dosage: '30–60 mg', frequency: 'daily', category: 'SNRI' },
  { name: 'Cyclobenzaprine', dosage: '5–10 mg', frequency: 'at bedtime', category: 'Muscle relaxer' },
]

const IC: MedSuggestion[] = [
  { name: 'Pentosan polysulfate (Elmiron)', dosage: '100 mg', frequency: 'three times daily', category: 'Bladder' },
  { name: 'Amitriptyline', dosage: '10–25 mg', frequency: 'at bedtime', category: 'Nerve pain' },
  { name: 'Hydroxyzine', dosage: '25 mg', frequency: 'at bedtime', category: 'Antihistamine' },
  { name: 'Phenazopyridine (AZO)', dosage: '100–200 mg', frequency: 'as needed', category: 'Urinary pain' },
]

const PAINFUL_PERIODS: MedSuggestion[] = [
  { name: 'Ibuprofen', dosage: '400–600 mg', frequency: 'every 6 hrs during period', category: 'NSAID' },
  { name: 'Naproxen', dosage: '250–500 mg', frequency: 'twice daily during period', category: 'NSAID' },
  { name: 'Mefenamic acid', dosage: '500 mg', frequency: 'three times daily', category: 'NSAID' },
  { name: 'Acetaminophen', dosage: '500–1000 mg', frequency: 'as needed', category: 'Analgesic' },
  { name: 'Tranexamic acid', dosage: '1000 mg', frequency: 'during heavy flow', category: 'Antifibrinolytic' },
]

// Map internal condition codes (from intake / profile) → suggestion bundles.
// Covers both `prior_diagnoses` codes and `what_brings_you` + `conditions`
// codes so we catch the user regardless of where they declared it.
const BY_CONDITION: Record<string, MedSuggestion[]> = {
  endometriosis: ENDO_ADENO,
  adenomyosis: ENDO_ADENO,
  pcos: PMOS,
  fibroids: FIBROIDS,
  chronic_pelvic_pain: CPP,
  pelvic_pain: CPP,
  pelvic_floor_dysfunction: CPP,
  interstitial_cystitis: IC,
  painful_periods: PAINFUL_PERIODS,
  cramps: PAINFUL_PERIODS,
  irregular_cycles: PMOS,
  hormonal_symptoms: PMOS,
}

export function suggestionsForConditions(
  conditions: readonly string[] | undefined | null,
  cap = 8,
): MedSuggestion[] {
  if (!conditions || conditions.length === 0) return PAINFUL_PERIODS.slice(0, cap)
  const seen = new Set<string>()
  const out: MedSuggestion[] = []
  for (const c of conditions) {
    const bundle = BY_CONDITION[c]
    if (!bundle) continue
    for (const s of bundle) {
      if (seen.has(s.name)) continue
      seen.add(s.name)
      out.push(s)
      if (out.length >= cap) return out
    }
  }
  if (out.length === 0) return PAINFUL_PERIODS.slice(0, cap)
  return out
}
